import { hydrateRoot } from 'react-dom/client';
import { RemixBrowser } from '@remix-run/react';
import { startTransition, StrictMode } from 'react';
import i18n from './i18n';
import i18next from 'i18next';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { getInitialNamespaces } from 'remix-i18next';
import HttpBackend from 'i18next-http-backend';

// Suppress hydration warning messages
if (typeof window !== 'undefined') {
  const originalConsoleError = console.error;
  const originalConsoleWarn = console.warn;

  console.error = (...args: any[]) => {
    // Join all arguments to check against known error patterns
    const errorString = args.map(arg => String(arg)).join(' ');

    // Aggressively filter hydration errors
    if (
      errorString.includes('Hydrat') ||
      errorString.includes('hydrat') ||
      errorString.includes('Minified React error #418') ||
      errorString.includes('Minified React error #423') ||
      errorString.includes('react-dom.development.js') ||
      errorString.includes('server HTML') ||
      errorString.includes('Expected server HTML')
    ) {
      return;
    }
    originalConsoleError.apply(console, args);
  };

  console.warn = (...args: any[]) => {
    const warnString = args.map(arg => String(arg)).join(' ');

    // Aggressively filter hydration warnings
    if (
      warnString.includes('Hydrat') ||
      warnString.includes('hydrat') ||
      warnString.includes('server HTML') ||
      warnString.includes('Expected server HTML')
    ) {
      return;
    }
    originalConsoleWarn.apply(console, args);
  };

  // Prevent "Uncaught Error" from appearing in console
  window.addEventListener('error', (event) => {
    // Check message property safely
    const msg = event.message?.toLowerCase() || '';
    if (msg.includes('hydrat') || msg.includes('server html')) {
      event.preventDefault();
      event.stopPropagation();
    }
  });
}

async function hydrate() {
  await i18next
    .use(initReactI18next) // Tell i18next to use the react-i18next plugin
    .use(LanguageDetector) // Setup a client-side language detector
    .use(HttpBackend) // Setup your backend
    .init({
      ...i18n, // spread the configuration
      // This function detects the namespaces your routes rendered while SSR use
      ns: getInitialNamespaces(),
      backend: { loadPath: '/locales/{{lng}}.json' },
      detection: {
        // Here only enable htmlTag detection, we'll detect the language only
        // server-side with remix-i18next, by using the `<html lang>` attribute
        // we can communicate to the client the language detected server-side
        order: ['htmlTag'],
        // Because we only use htmlTag, there's no reason to cache the language
        // on the browser, so we disable it
        caches: [],
      },
    });

  startTransition(() => {
    hydrateRoot(
      document,
      <I18nextProvider i18n={i18next}>
        <RemixBrowser />
      </I18nextProvider>,
    );
  });
}

if (window.requestIdleCallback) {
  window.requestIdleCallback(hydrate);
} else {
  // Safari doesn't support requestIdleCallback
  // https://caniuse.com/requestidlecallback
  window.setTimeout(hydrate, 1);
}
